import { dmyhm } from "../domain/format";
import { hasSample, setOpenWith, totals } from "../domain/library";
import type { OpenWith } from "../domain/types";
import { addSampleData, removeSampleData } from "../sample/sample-data";
import { ctx } from "./context";
import { ic } from "./icons";
import { downloadBackup, openExportText, openImportSheet, refreshSafety, safety, undoLastImport } from "./import-export";
import { toast } from "./shell";
import { failNotice, failOf } from "./failure";
import { failReasonOf } from "../storage/errors";
import { accountSettingsHTML } from "./account";
import { topBar } from "./app";
import { startBlankLibrary } from "./welcome";

const OPTS: { k: OpenWith; t: string; d: string; real?: boolean }[] = [
  { k: "default", t: "เบราว์เซอร์หลักของเครื่อง", d: "เปิดเป็นแท็บใหม่", real: true },
  { k: "chrome", t: "Chrome", d: "แนวคิด ยังไม่เชื่อมจริง" },
  { k: "safari", t: "Safari", d: "แนวคิด ยังไม่เชื่อมจริง" },
  { k: "app", t: "แอปที่ตรงกับลิงก์", d: "เช่น เปิด Sheet ในแอป Google Sheets · แนวคิด ยังไม่เชื่อมจริง" }
];

/** two-tap confirm used by destructive buttons, same pattern as the prototype's delete */
function armed(el: HTMLElement, label: string): boolean {
  if (el.classList.contains("arm")) return true;
  el.classList.add("arm"); el.innerHTML = ic("trash") + label; return false;
}

export function renderSettings($app: HTMLElement) {
  const { store } = ctx, S = store.lib, t = totals(S);
  const sampleCount = S.workspaces.filter(x => x.sample).length + S.categories.filter(x => x.sample).length + S.actions.filter(x => x.sample).length;
  const cloud = ctx.mode === "cloud";
  $app.innerHTML = topBar()
    + accountSettingsHTML()
    + `<div class="set"><h2>${ic("ext")}เปิดลิงก์ด้วย <span class="tag">แนวคิด</span></h2>
       <p>ตอนนี้ทุกลิงก์เปิดเป็นแท็บใหม่ในเบราว์เซอร์ ตัวเลือกอื่นเก็บค่าไว้ได้แต่ยังไม่มีผลกับการเปิดลิงก์</p>
       <div class="panel">${OPTS.map(o => `<label class="opt"><input type="radio" name="openwith" id="ow-${o.k}" value="${o.k}" ${S.settings.openWith === o.k ? "checked" : ""}><span class="ot"><b>${o.t}</b><small>${o.d}</small></span>${o.real ? `<span class="tag okk">ใช้ได้</span>` : `<span class="tag">ยังไม่เชื่อม</span>`}</label>`).join("")}</div></div>`
    + `<div class="set" id="set-backup"><h2>${ic("folder")}สำรองและนำเข้าข้อมูล</h2>
       <p>ไฟล์ JSON เก็บทุกสถานที่ หมวด และ Action พร้อม ID และวันที่ ใช้ย้ายเบราว์เซอร์หรือเครื่อง และย้ายข้อมูลจากต้นแบบ</p>
       <div class="stack">
         <button class="btn primary" data-do="export" id="btn-export">${ic("ext")}ส่งออกไฟล์สำรอง (.json)</button>
         <button class="btn" data-do="export-text" id="btn-export-text">${ic("copy")}คัดลอกเป็นข้อความ</button>
         <button class="btn" data-do="import" id="btn-import">${ic("plus")}นำเข้าจากไฟล์ JSON</button>
         ${safety ? `<button class="btn ghost" data-do="undo-import" id="btn-undo-import">${ic("left")}ย้อนกลับเป็นข้อมูล${safety.reason} (${dmyhm(safety.savedAt)})</button>` : ""}
       </div>
       <p class="note" style="margin-top:10px">ก่อนนำเข้า ระบบตรวจรูปแบบไฟล์และสรุปผลให้ยืนยันก่อนเสมอ รายการที่ ID ซ้ำจะไม่ถูกเพิ่มซ้ำ และไม่ถูกเขียนทับถ้าไม่ได้เลือก</p></div>`
    + `<div class="set" id="set-sample"><h2>${ic("sparkles")}ข้อมูลตัวอย่าง</h2>
       ${hasSample(S)
         ? `<p>ตอนนี้มีรายการตัวอย่าง ${sampleCount} รายการ ลบได้โดยไม่กระทบรายการที่คุณสร้างเอง</p><button class="btn danger" data-do="sample-remove" id="btn-sample-remove" style="width:100%">${ic("trash")}ลบเฉพาะข้อมูลตัวอย่าง</button>`
         : `<p>ไม่มีข้อมูลตัวอย่าง${cloud ? "ในบัญชีนี้" : "ในเครื่องนี้"} เพิ่มได้เพื่อลองใช้งาน รายการของคุณจะไม่ถูกแตะ</p><button class="btn" data-do="sample-add" id="btn-sample-add" style="width:100%">${ic("plus")}เพิ่มข้อมูลตัวอย่าง</button>`}</div>`
    + `<div class="set"><h2>${ic("info")}ข้อมูลเก็บที่ไหน</h2>
       <div class="panel"><dl class="kv">
         ${cloud ? `<dt>ที่เก็บ</dt><dd id="set-store">${store.repo.label} (Supabase)</dd>
         <dt>สถานะ</dt><dd>${ctx.sessionLost ? `<span class="tag bad">เซสชันหมดอายุ</span>` : `<span class="tag okk">เชื่อมต่อบัญชีแล้ว</span>`}</dd>
         <dt>หลัง refresh</dt><dd>ข้อมูลอยู่บน Cloud เปิดอีกเครื่องด้วยบัญชีเดียวกันเห็นข้อมูลเดียวกัน (ต้อง refresh เพื่อเห็นการเปลี่ยนจากเครื่องอื่น)</dd>
         <dt>บันทึก</dt><dd>ขึ้น “บันทึกแล้ว” เมื่อ Cloud รับแล้วเท่านั้น ถ้าเน็ตหลุดจะไม่บันทึกและให้ลองใหม่ (ยังไม่มีคิวบันทึกออฟไลน์)</dd>`
         : `<dt>ที่เก็บ</dt><dd>${store.repo.label} เท่านั้น ไม่ส่งขึ้นเซิร์ฟเวอร์</dd>
         <dt>สถานะ</dt><dd>${store.storageOK ? `<span class="tag okk">บันทึกได้</span>` : `<span class="tag bad">บันทึกไม่ได้</span>`}</dd>
         <dt>หลัง refresh</dt><dd>${store.storageOK ? "ข้อมูลยังอยู่ ในเบราว์เซอร์และอุปกรณ์เดิม" : "ข้อมูลจะหายเมื่อปิดหน้า เพราะเบราว์เซอร์นี้ไม่อนุญาตให้บันทึก"}</dd>
         <dt>หายเมื่อ</dt><dd>ล้างข้อมูลเว็บ เปิดโหมดส่วนตัว หรือเปิดจากเครื่อง/เบราว์เซอร์อื่น (ไม่ซิงก์) ส่งออกไฟล์สำรองไว้เสมอ</dd>`}
         <dt>ตอนนี้มี</dt><dd id="set-counts">${t.workspaces} สถานที่ · ${t.categories} หมวด · ${t.actions} Action</dd>
         ${store.lastSaved ? `<dt>บันทึกล่าสุด</dt><dd>${dmyhm(store.lastSaved)}</dd>` : ""}
       </dl></div></div>`
    + `<div class="set"><h2>${ic("info")}ยังไม่มีในเวอร์ชันนี้</h2>
       <div class="panel"><ul class="todo">${cloud ? "<li>ซิงก์ทันทีแบบ realtime และคิวบันทึกออฟไลน์</li><li>ทีม</li>" : "<li>บัญชีผู้ใช้และซิงก์ผ่าน cloud</li>"}<li>แพ็กเกจชำระเงิน</li><li>Secret Zone</li><li>หน้าจอ Desktop</li><li>Production deploy</li></ul></div></div>`
    + `<div class="set"><h2>${ic("trash")}ล้างข้อมูลทั้งหมด</h2><p>ลบทุกอย่าง${cloud ? "ในบัญชีนี้บน Cloud (ทุกเครื่อง)" : "ในเบราว์เซอร์นี้"}แล้วกลับไปหน้าเริ่มต้น ระบบเก็บสำเนาไว้ย้อนกลับได้ 1 ครั้ง แต่ควรส่งออกไฟล์สำรองก่อน</p>
       <button class="btn danger" data-do="wipe" id="btn-wipe" style="width:100%">${ic("trash")}ล้างข้อมูลทั้งหมด</button></div>`;
}

export function handleSettingsAction(act: string, el: HTMLElement): boolean {
  const { store, V } = ctx;
  switch (act) {
    case "export": downloadBackup(); return true;
    case "export-text": openExportText(); return true;
    case "import": openImportSheet(); return true;
    case "undo-import":
      if (!armed(el, "แตะอีกครั้งเพื่อย้อนกลับ (ข้อมูลตอนนี้จะถูกแทนที่)")) return true;
      void undoLastImport(); return true;
    case "sample-add":
      void store.replace(addSampleData(store.lib)).then(r => { ctx.render(); if (r.ok) toast("เพิ่มข้อมูลตัวอย่างแล้ว"); else failNotice(r); });
      return true;
    case "sample-remove": {
      if (!armed(el, "แตะอีกครั้งเพื่อลบข้อมูลตัวอย่าง")) return true;
      const r = removeSampleData(store.lib);
      const next = r.library.workspaces.length ? r.library : startBlankLibrary(r.library);
      void store.replace(next).then(res => { ctx.render(); if (!res.ok) { failNotice(res); return; } toast(r.kept ? `ลบตัวอย่างแล้ว เก็บ ${r.kept} หมวด/สถานที่ที่มีรายการของคุณไว้` : "ลบข้อมูลตัวอย่างแล้ว"); });
      return true; }
    case "wipe":
      if (!armed(el, "แตะอีกครั้งเพื่อยืนยันล้างข้อมูล")) return true;
      void (async () => {
        try { await store.repo.saveSafetyCopy(store.lib, "ก่อนล้างข้อมูลทั้งหมด"); }
        catch (e) {
          ctx.render();
          if (failReasonOf(e) === "storage") toast("ยังไม่ล้าง: เก็บสำเนาไว้ย้อนกลับไม่ได้ ข้อมูลยังอยู่ครบ"); else failNotice(failOf(e));
          return;
        }
        const res = await store.clearAll();
        if (!res.ok) { ctx.render(); if (res.reason === "storage") toast("ล้างข้อมูลไม่ได้ เบราว์เซอร์ปฏิเสธ ข้อมูลยังอยู่ครบ"); else failNotice(res); return; }
        await refreshSafety();
        V.view = "welcome"; V.catId = null; ctx.render(); scrollTo(0, 0); toast("ล้างข้อมูลแล้ว");
      })();
      return true;
  }
  return false;
}

document.addEventListener("change", e => {
  const t = e.target as HTMLInputElement;
  if (t.name === "openwith") {
    void ctx.store.commit(l => setOpenWith(l, t.value as OpenWith)).then(r => {
      if (!r.ok) { ctx.render(); failNotice(r); return; } // re-render puts the radio back on the saved value
      toast(t.value === "default" ? "ใช้เบราว์เซอร์หลัก" : "เก็บค่าไว้แล้ว แต่ยังไม่เชื่อมจริง");
    });
  }
});
